import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Browser } from '@capacitor/browser';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import type { MatchInfo } from '../screens/Discover';
import { Portrait } from '../components/Portrait';
import { Img } from '../components/Img';
import { Chip, SoftButton } from '../components/common';
import { Icon } from '../components/icons';
import { PoweredByGoogle } from '../components/GoogleBrand';
import { useAuth } from '../hooks/useAuth';
import { DateScheduler } from './DateScheduler';
import { hapticMedium } from '../lib/haptics';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface VenueData {
  name: string;
  address: string;
  priceRange: string;
  rating?: number;
  photos?: string[];
  category?: string;
  mapsUrl?: string;
  website?: string;
}

interface DatePlanData {
  id: string;
  planTitle: string;
  planDescription: string;
  compatibilityReason: string;
  activitySuggestion: string | null;
  groupSize: number;
  swapsRemaining: number;
  venuePrimary: VenueData | null;
  venueAlternative: VenueData | null;
  venuePrimaryRaw: { name: string; address: string; priceRange: string } | null;
  venueAlternativeRaw: { name: string; address: string; priceRange: string } | null;
}

interface DateSheetProps {
  match: MatchInfo;
  t: Theme;
  onClose: () => void;
  initialPlan?: DatePlanData;
}

const PRICE_LABEL: Record<string, string> = {
  BUDGET: '£', MODERATE: '££', PREMIUM: '£££', LUXURY: '££££',
};

const CATEGORY_LABEL: Record<string, string> = {
  RESTAURANT: 'dinner', BAR: 'drinks', CULTURE: 'culture',
  OUTDOOR: 'outdoors', EXPERIENCE: 'activity', WELLNESS: 'wellness', EVENT_SPACE: 'event',
};

export function DateSheet({ match: m, t, onClose, initialPlan }: DateSheetProps) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [show, setShow] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<'primary' | 'alternative'>('primary');
  const [openScheduler, setOpenScheduler] = useState(false);
  const [proposed, setProposed] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    console.log('[DateSheet] handleRegenerate — calling POST /date-plan?force=true');
    setRegenerating(true);
    setSelectedVenue('primary');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/${m.matchId}/date-plan?force=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[DateSheet] regenerate response status:', res.status);
      const body = await res.json().catch(() => ({}));
      console.log('[DateSheet] regenerate response body:', body);
      // Remove stale plan from cache so polling restarts
      queryClient.setQueryData(['match-plan', m.matchId], (old: any) =>
        old ? { ...old, datePlan: null } : old
      );
      queryClient.invalidateQueries({ queryKey: ['match-plan', m.matchId] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    } catch (e) {
      console.error('[DateSheet] regenerate error:', e);
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => { const id = setTimeout(() => setShow(true), 40); return () => clearTimeout(id); }, []);

  // Poll the match until datePlan is available (AI generation is async)
  const { data: matchData, isLoading } = useQuery({
    queryKey: ['match-plan', m.matchId],
    queryFn: async () => {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/api/matches/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const { matches } = await res.json();
      return matches.find((mx: any) => mx.matchId === m.matchId || mx.otherUser?.id === m.matchId) || null;
    },
    refetchInterval: (query) => {
      // Keep polling every 3s until datePlan arrives
      const data = query.state.data as any;
      return data?.datePlan ? false : 3000;
    },
    staleTime: 0,
  });

  // Kick off date plan generation once we have the real matchId from the poll.
  // m.matchId from the match popup is a userId (not a match UUID) because
  // processLike returns before the Match row is created. Using matchData.matchId
  // ensures we always POST to the correct UUID.
  useEffect(() => {
    if (initialPlan || !matchData || matchData.datePlan) return;
    getIdToken().then(token => {
      fetch(`${API_BASE}/api/matches/${matchData.matchId}/date-plan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    });
  }, [matchData?.matchId]);

  // While regenerating show spinner; after, prefer fresh matchData over initialPlan
  const datePlan: DatePlanData | null = regenerating ? null : (matchData?.datePlan || initialPlan || null);
  // Fall back to the raw venue (name/address only) when the DB venue object is missing —
  // e.g. importGoogleVenue failed, so we still show the name rather than nothing.
  const primaryVenue: VenueData | null = datePlan?.venuePrimary || datePlan?.venuePrimaryRaw || null;
  const altVenue: VenueData | null = datePlan?.venueAlternative || datePlan?.venueAlternativeRaw || null;
  const venue = selectedVenue === 'primary' ? primaryVenue : altVenue;
  const otherVenue = selectedVenue === 'primary' ? altVenue : primaryVenue;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute', inset: 0, zIndex: 210,
        background: 'rgba(31,26,23,0.55)', backdropFilter: 'blur(8px)',
        display: 'flex', flexDirection: 'column',
        opacity: show ? 1 : 0, transition: 'opacity 200ms',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          marginTop: 64, background: t.paper, borderRadius: '28px 28px 0 0',
          flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transform: show ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 380ms cubic-bezier(0.2,0.8,0.2,1)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '10px 20px 10px', flexShrink: 0 }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: t.line, margin: '0 auto 12px' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 100,
                background: `conic-gradient(from 220deg, ${t.blush}, ${t.terracotta}, ${t.gold}, ${t.blush})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ width: 30, height: 30, borderRadius: 100, background: t.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Icon.sparkle({ s: 15, c: t.terracotta })}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 10.5, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkMute }}>Concierge</div>
                <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 18, color: t.ink, lineHeight: 1 }}>First date, thoughtfully drafted.</div>
              </div>
            </div>
            <button onClick={onClose} style={{
              all: 'unset', cursor: 'pointer',
              width: 34, height: 34, borderRadius: 100,
              background: t.card, border: `1px solid ${t.line}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{Icon.x({ s: 16, c: t.ink })}</button>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 120px' }} className="scroll-container">

          {/* Loading state — only show spinner if still fetching AND no plan yet */}
          {(isLoading || !datePlan) && (
            <div style={{ padding: '48px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 100,
                background: `conic-gradient(from 220deg, ${t.blush}, ${t.terracotta}, ${t.gold}, ${t.blush})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'spin 2s linear infinite',
              }}>
                <div style={{ width: 34, height: 34, borderRadius: 100, background: t.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Icon.sparkle({ s: 16, c: t.terracotta })}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 20, color: t.ink, marginBottom: 6 }}>
                  Concierge is thinking...
                </div>
                <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute }}>
                  Crafting a date plan for you and {m.displayName}
                </div>
              </div>
            </div>
          )}

          {datePlan && (
            <>
              {/* Why card */}
              <div style={{
                padding: '16px 18px', borderRadius: 18,
                background: t.card, border: `1px solid ${t.line}`,
                display: 'flex', gap: 12, alignItems: 'flex-start',
                marginBottom: 16,
              }}>
                {m.photos?.[0] ? (
                  <Img placeholder={t.paperAlt} src={m.photos[0]} alt={m.displayName} style={{ width: 42, height: 42, borderRadius: 100, flexShrink: 0, objectFit: 'cover' }} />
                ) : (
                  <Portrait seed={m.matchId.slice(0, 4)} style={{ width: 42, height: 42, borderRadius: 100, flexShrink: 0 }} />
                )}
                <div>
                  <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.inkMute, marginBottom: 2 }}>
                    Because you and {m.displayName}...
                  </div>
                  <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 17, color: t.ink, lineHeight: 1.3 }}>
                    "{datePlan.compatibilityReason}"
                  </div>
                </div>
              </div>

              {/* Title + vibe */}
              <h2 style={{
                fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400,
                fontSize: 32, margin: '4px 0 4px', color: t.ink, lineHeight: 1.05, letterSpacing: -0.4,
              }}>{datePlan.planTitle}</h2>
              <div style={{ fontFamily: FONTS.sans, fontSize: 13, color: t.inkSoft, marginBottom: 6 }}>
                {datePlan.planDescription}
              </div>
              <div style={{ display: 'flex', gap: 6, margin: '10px 0 20px', flexWrap: 'wrap' }}>
                <Chip t={t} small>Chosen by Concierge</Chip>
                {datePlan.groupSize === 4 && <Chip t={t} small>Double date</Chip>}
                {venue?.priceRange && <Chip t={t} small>{PRICE_LABEL[venue.priceRange] || venue.priceRange} per head</Chip>}
              </div>

              {/* Venue cards — only if venues were found */}
              {(primaryVenue || altVenue) && (
                <>
                  {altVenue && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }} className="scroll-container">
                      {(['primary', 'alternative'] as const).map((v) => {
                        const vn = v === 'primary' ? primaryVenue : altVenue;
                        return (
                          <button key={v} onClick={() => setSelectedVenue(v)} style={{
                            all: 'unset', cursor: 'pointer',
                            padding: '8px 14px', borderRadius: 100, whiteSpace: 'nowrap',
                            fontFamily: FONTS.sans, fontSize: 12.5, fontWeight: 600,
                            background: selectedVenue === v ? t.ink : 'transparent',
                            color: selectedVenue === v ? t.paper : t.ink,
                            border: `1px solid ${selectedVenue === v ? t.ink : t.line}`,
                            flexShrink: 0, transition: 'all 150ms',
                          }}>{vn?.name || (v === 'primary' ? 'Option A' : 'Option B')}</button>
                        );
                      })}
                    </div>
                  )}
                  {venue && (
                    <div style={{ position: 'relative', marginBottom: 20 }}>
                      {venue.photos?.[0] && (
                        <div style={{ borderRadius: '14px 14px 0 0', overflow: 'hidden', height: 160 }}>
                          <Img placeholder={t.paperAlt} src={venue.photos[0]} alt={venue.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <div style={{
                        padding: '14px 16px 16px', background: t.card, border: `1px solid ${t.line}`,
                        borderRadius: venue.photos?.[0] ? '0 0 14px 14px' : 14,
                        borderTop: venue.photos?.[0] ? 'none' : undefined,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 22, color: t.ink, lineHeight: 1.1 }}>{venue.name}</div>
                          <span style={{ fontFamily: FONTS.mono, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', padding: '2px 6px', borderRadius: 3, flexShrink: 0, background: t.paperAlt, color: t.inkSoft }}>
                            {venue.category ? (CATEGORY_LABEL[venue.category] || venue.category.toLowerCase()) : 'venue'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          {venue.rating && venue.rating > 0 && <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: t.gold, fontWeight: 600 }}>★ {venue.rating.toFixed(1)}</span>}
                          <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: t.inkMute }}>{PRICE_LABEL[venue.priceRange] || venue.priceRange}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONTS.sans, fontSize: 12, color: t.inkSoft }}>
                          {Icon.pin({ s: 12, c: t.inkMute })} {venue.address}
                        </div>
                        {venue.mapsUrl && (
                          <button
                            onClick={() => Browser.open({ url: venue.mapsUrl! })}
                            style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, fontFamily: FONTS.sans, fontSize: 12, color: t.terracotta, fontWeight: 600 }}
                          >
                            Open in Maps →
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {otherVenue && datePlan.swapsRemaining > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <button onClick={() => setSelectedVenue(v => v === 'primary' ? 'alternative' : 'primary')} style={{
                        all: 'unset', cursor: 'pointer', padding: '8px 12px', borderRadius: 100,
                        fontFamily: FONTS.sans, fontSize: 12, fontWeight: 500,
                        color: t.ink, border: `1px dashed ${t.line}`, background: 'transparent',
                      }}>↻ Try {otherVenue.name} instead</button>
                    </div>
                  )}
                  {/* Places API policy: venue data shown without a Google Map
                      requires a visible "Powered by Google" credit. */}
                  <PoweredByGoogle t={t} style={{ marginBottom: 14 }} />
                </>
              )}

              {/* Activity suggestion */}
              {datePlan.activitySuggestion && (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 20, padding: '14px 16px', borderRadius: 14, background: t.card, border: `1px solid ${t.line}` }}>
                  <div style={{ width: 36, height: 36, borderRadius: 100, background: t.paperAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {Icon.sparkle({ s: 16, c: t.terracotta })}
                  </div>
                  <div>
                    <div style={{ fontFamily: FONTS.sans, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: t.inkMute, marginBottom: 3 }}>Also suggested</div>
                    <div style={{ fontFamily: FONTS.sans, fontSize: 13.5, color: t.ink, lineHeight: 1.4 }}>{datePlan.activitySuggestion}</div>
                  </div>
                </div>
              )}

              {/* Info note */}
              <div style={{
                marginTop: 10, padding: '12px 14px', borderRadius: 12,
                background: t.paperAlt, display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                {Icon.sparkle({ s: 14, c: t.terracotta })}
                <div style={{ fontFamily: FONTS.sans, fontSize: 11.5, color: t.inkSoft, lineHeight: 1.5 }}>
                  If you both tap "I'd go" — we'll send the plan to your chat. No pressure, just a great night out.
                </div>
              </div>

              {/* Something else */}
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                style={{
                  all: 'unset', cursor: regenerating ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  width: '100%', boxSizing: 'border-box',
                  marginTop: 10, padding: '11px 0', borderRadius: 12,
                  border: `1px dashed ${t.line}`, background: 'transparent',
                  fontFamily: FONTS.sans, fontSize: 13, color: t.inkMute,
                  opacity: regenerating ? 0.5 : 1,
                  transition: 'opacity 150ms',
                }}
              >
                {regenerating
                  ? <><span style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${t.terracotta}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Thinking of something new…</>
                  : <>↺ Something else</>
                }
              </button>
            </>
          )}
        </div>

        {/* Sticky CTA */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 20px 30px',
          background: `linear-gradient(to top, ${t.paper} 70%, transparent)`,
          display: 'flex', gap: 10,
        }}>
          <SoftButton t={t} variant="outline" onClick={onClose} style={{ flex: 1 }}>Just chat</SoftButton>
          {proposed ? (
            <SoftButton t={t} variant="outline" style={{ flex: 1.3, opacity: 0.6 }}>
              Proposal sent ✓
            </SoftButton>
          ) : (
            <SoftButton
              t={t} variant="terracotta" style={{ flex: 1.3 }}
              icon={Icon.heart({ s: 16, c: t.paper, fill: t.paper, w: 0 })}
              onClick={datePlan ? () => { hapticMedium(); setOpenScheduler(true); } : undefined}
            >
              I'd go
            </SoftButton>
          )}
        </div>

        {/* Date Scheduler overlay — rendered inside the sheet so it layers on top */}
        {openScheduler && datePlan && (
          <DateScheduler
            matchId={m.matchId}
            datePlanId={datePlan.id}
            otherUserName={m.displayName}
            groupSize={datePlan.groupSize}
            venues={[
              datePlan.venuePrimary ?? datePlan.venuePrimaryRaw ?? null,
              datePlan.venueAlternative ?? datePlan.venueAlternativeRaw ?? null,
            ].filter((v): v is NonNullable<typeof v> => v !== null)}
            t={t}
            onClose={() => setOpenScheduler(false)}
            onProposed={() => { setOpenScheduler(false); setProposed(true); }}
          />
        )}
      </div>
    </div>
  );
}
